import { entity, persistence } from "simpler-state"

export type KindFilter = "deduction" | "invoice" | "prepayment" | "repayment" | "correction" | "unknown"
export type MatchingFilter = "matched" | "unmatched"
export type CodingFilter = "coded" | "uncoded"

export interface FilterState {
  kinds: Set<KindFilter>
  matching: MatchingFilter | null
  coding: CodingFilter | null
  /** When set, only these import file row ids are shown (e.g. from Import results). */
  row_ids: Set<string> | null
}

interface SerializedFilterState {
  kinds: KindFilter[]
  matching: MatchingFilter | null
  coding: CodingFilter | null
  row_ids: string[] | null
}

export function createEmptyFilter(): FilterState {
  return { kinds: new Set(), matching: null, coding: null, row_ids: null }
}

export function isFilterEmpty(filter: FilterState): boolean {
  return (
    filter.kinds.size === 0 &&
    filter.matching === null &&
    filter.coding === null &&
    (filter.row_ids == null || filter.row_ids.size === 0)
  )
}

export function filter_for_import_result_row_ids(row_ids: string[]): FilterState {
  return { kinds: new Set(), matching: null, coding: null, row_ids: new Set(row_ids) }
}

const INITIAL_STATE: SerializedFilterState = {
  kinds: [],
  matching: null,
  coding: null,
  row_ids: null,
}

function deserializeFn(blob: string): SerializedFilterState {
  try {
    const data = JSON.parse(blob)
    return {
      kinds: Array.isArray(data.kinds) ? data.kinds : [],
      matching: data.matching ?? null,
      coding: data.coding ?? null,
      row_ids: Array.isArray(data.row_ids) && data.row_ids.length > 0 ? data.row_ids : null,
    }
  } catch {
    return INITIAL_STATE
  }
}

const FilterStateEntity = entity(INITIAL_STATE, [
  persistence("file_import_filter_state", { deserializeFn }),
])

export const FileImportFilterState = {
  use(): FilterState {
    const serialized = FilterStateEntity.use()
    return {
      kinds: new Set(serialized.kinds),
      matching: serialized.matching,
      coding: serialized.coding,
      row_ids: serialized.row_ids?.length ? new Set(serialized.row_ids) : null,
    }
  },

  get(): FilterState {
    const serialized = FilterStateEntity.get()
    return {
      kinds: new Set(serialized.kinds),
      matching: serialized.matching,
      coding: serialized.coding,
      row_ids: serialized.row_ids?.length ? new Set(serialized.row_ids) : null,
    }
  },

  set(filter: FilterState) {
    FilterStateEntity.set({
      kinds: Array.from(filter.kinds),
      matching: filter.matching,
      coding: filter.coding,
      row_ids: filter.row_ids?.size ? Array.from(filter.row_ids) : null,
    })
  },

  reset() {
    FilterStateEntity.set(INITIAL_STATE)
  },
}
